import { format, addSeconds, differenceInSeconds, startOfYear, startOfMonth, startOfWeek, endOfWeek, getWeek, isLeapYear, addMonths, addYears } from 'date-fns';

// Helper to determine if input is seconds or milliseconds
export const isMilliseconds = (ts: number): boolean => {
  // If timestamp is roughly after year 2286 (in seconds), we assume it's ms.
  // 10,000,000,000 seconds is year 2286. 
  // 1,000,000,000,000 milliseconds is year 2001.
  return Math.abs(ts) > 100000000000;
};

export const normalizeTimestamp = (ts: number): number => {
  return isMilliseconds(ts) ? Math.floor(ts / 1000) : ts;
};

export const formatSafeDate = (date: Date, fmt: string = 'yyyy-MM-dd HH:mm:ss'): string => {
  try {
    return format(date, fmt);
  } catch (e) {
    return 'Invalid Date';
  }
};

export const getLegacyFormats = (epoch: number) => {
  // Epoch is in seconds
  const ms = epoch * 1000;
  
  // LDAP: 100-nanosecond intervals since Jan 1, 1601
  // Unix epoch (1970) is 11,644,473,600 seconds after 1601
  const ldap = (epoch + 11644473600) * 10000000;

  // WebKit: Microseconds since Jan 1, 1601
  const webkit = (epoch + 11644473600) * 1000000;

  // Cocoa: Seconds since Jan 1, 2001
  // 2001 is 978307200 seconds after 1970
  const cocoa = epoch - 978307200;

  // HFS+: Seconds since Jan 1, 1904
  // 1904 is 2082844800 seconds before 1970 (Wait, 1970 is after 1904)
  // 1970 - 1904 = 66 years. 
  // epoch (1970 based) + difference = HFS
  const hfs = epoch + 2082844800;

  // Excel: Days since Dec 30, 1899
  // 25569 is days between 1899-12-30 and 1970-01-01
  const excel = (epoch / 86400) + 25569;

  return { ldap, webkit, cocoa, hfs, excel };
};

export const secondsToDuration = (seconds: number) => {
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  
  const parts = [];
  if (d > 0) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
  if (s > 0) parts.push(`${s} second${s !== 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(', ') : '0 seconds';
};

// Major time zones for the dropdown/table
export const MAJOR_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Australia/Sydney",
  "Asia/Dubai",
  "Asia/Kolkata",
  "America/Sao_Paulo",
  "Africa/Cairo",
  "Pacific/Auckland"
];

export const getCodeSnippets = (ts: number) => [
  { lang: 'Python', code: 'import time\nprint(int(time.time()))' },
  { lang: 'JavaScript', code: 'Math.floor(Date.now() / 1000)' },
  { lang: 'TypeScript', code: 'Math.floor(Date.now() / 1000)' },
  { lang: 'Java', code: 'System.currentTimeMillis() / 1000L' },
  { lang: 'Kotlin', code: 'System.currentTimeMillis() / 1000' },
  { lang: 'Swift', code: 'Int(Date().timeIntervalSince1970)' },
  { lang: 'Go', code: 'time.Now().Unix()' },
  { lang: 'C#', code: 'DateTimeOffset.UtcNow.ToUnixTimeSeconds()' },
  { lang: 'PHP', code: 'time()' },
  { lang: 'Ruby', code: 'Time.now.to_i' },
  { lang: 'Rust', code: 'use std::time::SystemTime;\nSystemTime::now().duration_since(SystemTime::UNIX_EPOCH).unwrap().as_secs()' },
  { lang: 'C++', code: 'std::time(nullptr)' },
  { lang: 'Objective-C', code: '[[NSDate date] timeIntervalSince1970]' },
  { lang: 'Dart', code: 'DateTime.now().millisecondsSinceEpoch ~/ 1000' },
  { lang: 'Lua', code: 'os.time()' },
  { lang: 'Perl', code: 'time' },
  { lang: 'R', code: 'as.numeric(Sys.time())' },
  { lang: 'SQL (MySQL)', code: 'SELECT UNIX_TIMESTAMP();' },
  { lang: 'PowerShell', code: '[int][double]::Parse((Get-Date -UFormat %s))' },
  { lang: 'Bash', code: 'date +%s' },
];